import { useCallback, useState } from "react";
import { useOnMount } from "~/common/hooks/useOnMount";
import type { TabDefinition } from "~/features/search/hooks/useSearchPageTabs";

interface Config {
  defaultTab: string;
  tabContainerId: string;
  trackTabActivation?: (tab: TabDefinition) => void;
  scrollOnPageLoad: boolean;
  /**
   * Server-resolved page anchor (e.g. {@code "#more-info"}) carried by the
   * route page-data. Replaces the former storefront page/tab-hash global; the
   * leading {@code "#"} is stripped to mirror the URL-hash branch.
   */
  pageAnchor?: string;
}

export const SCROLL_ON_PAGE_LOAD_TIMEOUT = 500;

export function useTabHashLogic(tabs: Record<string, TabDefinition>, config: Config) {
  const [activeTab, setActiveTab] = useState(() => getInitialTabFromUrl(tabs, config));

  const scrollToTabContainer = useCallback((immediate?: boolean) => {
    const fn = () => document.getElementById(config.tabContainerId)?.scrollIntoView();
    if (immediate) {
      fn();
    } else {
      window.setTimeout(fn, SCROLL_ON_PAGE_LOAD_TIMEOUT);
    }
  }, []);

  useOnMount(() => {
    if (!config.scrollOnPageLoad) {
      return;
    }
    const hasHash = !!window.location.hash;
    if (hasHash) {
      scrollToTabContainer();
    }
  });

  const trackTabActivation = config.trackTabActivation;
  const onTabChange = useCallback(
    (tabId: string, shouldScroll = false, shouldTrack = true) => {
      const tab = Object.values(tabs).find((tab) => tab.id === tabId)!;
      setActiveTab(tabId);
      if (shouldTrack) {
        trackTabActivation?.(tab);
      }
      window.location.hash = tabId;
      if (shouldScroll) {
        scrollToTabContainer(true);
      }
    },
    [setActiveTab, trackTabActivation, scrollToTabContainer],
  );

  return [activeTab, onTabChange] as const;
}

function getInitialTabFromUrl(tabs: Record<string, TabDefinition>, config: Config) {
  const hash = window.location.hash
    ? decodeURI(window.location.hash.substring(1))
    : config.pageAnchor && decodeURI(config.pageAnchor.substring(1));

  let tabId = config.defaultTab;

  if (hash) {
    const tab = Object.values(tabs).find((tab) => tab.id === hash);
    if (tab) {
      tabId = tab.id;
    }
  }

  return tabId;
}
